<template>
  <v-dialog
    v-model="isDialogVisible"
    width="50%"
    @click:outside="onHandleDialogAction"
    @keydown.esc="onHandleDialogAction"
  >
    <v-card>
      <v-toolbar
        color="blue-grey-darken-2"
      >
        <v-card-title>
          {{ $t('ErrorDialog.error_headline') }}
        </v-card-title>
      </v-toolbar>
      <div
        v-for="translatedMessage in translatedMessages"
        :key="translatedMessage"
        class="px-2 py-4 preserve-newlines"
      >{{ translatedMessage }}
      </div>

      <v-card-actions>
        <v-row
          align="center"
          justify="center"
          no-gutters
        >

          <v-btn
            color="primary"
            variant="text"
            @click="onHandleDialogAction"
          >
            {{ $t('ErrorDialog.confirm') }}
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  unref,
} from 'vue';

import {
  useI18n
} from '@/composables/useI18n';
import { ErrorCodes } from "@/Enums";
import { HttpError } from '@/scripts/Common/api';

const props = defineProps({
  error: {
    default: {
      message: 'server_error'
    },
    type: null,
  }
});

const emits = defineEmits([
  'onDialogCancelOrConfirm',
]);

const {
  t,
  te
} = useI18n();

interface ErrorWithCode {
  ErrorCode: ErrorCodes;
}

interface ErrorWithStatus {
  status: number;
}

const isDialogVisible = ref<boolean>(false);

const statusCodesToHandle = [413];

const getGenericErrorMessage = () => [t('ErrorDialog.server_error'), t('ErrorDialog.try_again_later')];

const getTranslationForErrorCodeOrDefault = (key: string) => {
  if (te(key)) {
    return [t(key)];
  }
  console.warn(`[vue-i18n] Cannot translate the value of keypath '${key}'.`);
  return getGenericErrorMessage();
};

const handleHttpError = (error: object): string[] | null => {
  if (error instanceof HttpError) {
    return getTranslationForErrorCodeOrDefault(`ErrorDialog.errorCodes.${error.problemDetails.type}`);
  }
  return null;
};

const handleErrorWithProperty = (error: object): string[] | null => {
  if ('error' in error) {
    return [(error as { error: string }).error];
  }
  return null;
};

const handleErrorCode = (error: object): string[] | null => {
  if ('ErrorCode' in error) {
    const errorWithCode = error as ErrorWithCode;
    if (errorWithCode.ErrorCode !== ErrorCodes.unknown) {
      return getTranslationForErrorCodeOrDefault(`ErrorDialog.errorCodes.${errorWithCode.ErrorCode}`);
    }
  }
  return null;
};

const handleStatusCode = (error: object): string[] | null => {
  if ('status' in error) {
    const errorWithStatus = error as ErrorWithStatus;
    if (statusCodesToHandle.includes(errorWithStatus.status)) {
      return getTranslationForErrorCodeOrDefault(`ErrorDialog.statusCodes.${errorWithStatus.status}`);
    }
  }
  return null;
};

const translatedMessages = computed(() => {
  const error = unref(props.error);

  if (error === null || typeof error !== 'object') {
    return getGenericErrorMessage();
  }

  return handleHttpError(error)
    ?? handleErrorWithProperty(error)
    ?? handleErrorCode(error)
    ?? handleStatusCode(error)
    ?? getGenericErrorMessage();
});

const onHandleDialogAction = () => {
  isDialogVisible.value = false;
  emits('onDialogCancelOrConfirm');
}

onMounted(() => {
  isDialogVisible.value = true;
});

onUnmounted(() => {
  isDialogVisible.value = false;
})
</script>

<style scoped>
.preserve-newlines {
  white-space: pre-line;
}
</style>
